import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "strategies",
  title: "Strategies and versions",
  navLabel: "Strategies",
  description:
    "Create executable strategies, review their rules and source, preserve versions, and move each version through testing and deployment.",
  section: "strategies",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: ["Mavori strategies", "create a trading strategy", "manage strategy versions"],
  related: ["agent", "backtesting", "deployments"],
  blocks: [
    {
      type: "lead",
      text: "A Strategy turns a market idea into executable rules you can inspect, test, and deploy. Mavori keeps the idea, source, versions, backtests, and deployment state together so each result stays tied to the exact rules that produced it.",
    },

    { type: "h2", text: "The strategy lifecycle" },
    {
      type: "ol",
      items: [
        "Create a Strategy by describing the idea to the [Agent](/docs/agent) or by choosing a template from **Explore**.",
        "Review its hypothesis, instruments, bar type, parameters, and source on **Setup**.",
        "Run the pre-check and a [backtest](/docs/backtesting) against one saved version.",
        "Choose an eligible Portfolio Account and create a [Strategy Deployment](/docs/deployments).",
        "Monitor the deployment, fills, and account impact from the Strategy and Portfolio pages.",
      ],
    },

    { type: "h2", text: "Create or explore" },
    {
      type: "p",
      text: "The Strategies page separates **Your strategies** from **Explore**. Your strategies are the artifacts you own and can continue refining. Explore is the template catalog: use its filters to find a starting method, inspect its rules, then create your own Strategy from it. A template is a starting structure, not a finished trading decision.",
    },
    {
      type: "p",
      text: "When starting in chat, describe the instruments, entry and exit logic, time horizon, and sizing or risk idea you care about. The Agent can ask for missing choices before it creates the first version. Open the resulting Strategy from the conversation or from the Strategies page.",
    },

    { type: "h2", text: "Review Setup and preserve versions" },
    {
      type: "p",
      text: "The **Setup** tab is the readable definition of the Strategy. Confirm that the hypothesis matches your intent, the instrument list is correct, the bar type fits the decision cadence, and the source implements the rules you described. You can edit eligible fields and source directly.",
    },
    {
      type: "p",
      text: "Fork a version before testing a meaningful alternative. Versions keep earlier rules and results available, which makes it possible to tell whether a change improved the idea or merely changed the test. The **Backtest** tab lets you select a version and read the run tied to it.",
    },

    { type: "h2", text: "Move from an idea to supervised execution" },
    {
      type: "p",
      text: "A saved Strategy does not run against an account by itself. First complete its checks and review its backtest. Then use **Deployment** to choose the Portfolio Account, capital, and active window. Deployment is a separate, explicit action because the account, permissions, and Risk Controls are part of the execution decision.",
    },
  ],
};
